import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { type TFunction } from 'i18next'

const styles = stylex.create({
  addonTransparentText: {
    opacity: 0,
    height: 10,
  },
  discountedPrice: {
    backgroundImage: 'linear-gradient(90deg, #FFF9BA 0%, #FFFBEC 100%)',
  },
})

export type PriceType = 'totalPrice' | 'originalPrice' | 'salePrice' | 'addonSalePrice' | 'productPageAddonSalePrice' | 'discountedPrice'

export interface CartSalePriceProps {
  'currency'?: string
  'salePrice'?: number
  'originalPrice'?: number
  'quantity'?: number
  'priceType'?: PriceType
  'data-testid'?: string
  'showTransparentText'?: boolean
  't'?: TFunction
}

const priceMap = (
  priceType: CartSalePriceProps['priceType'],
  currency: string,
  salePrice: number,
  originalPrice: number,
  quantity: number,
  dataTestId?: string,
  showTransparentText?: boolean,
  t?: TFunction,
) => {
  if (priceType === 'originalPrice') {
    return (
      <VStack alignItems="center">
        <Text data-testid={dataTestId} variant="caption" textDecoration="line-through" color={colors.grey600}>
          {formatPrice(
            currency,
            originalPrice,
          )}
        </Text>
      </VStack>
    )
  }

  if (priceType === 'salePrice') {
    return (
      <VStack>
        <Text data-testid={dataTestId} variant="caption" fontWeight={400} color={colors.grey600}>
          {formatPrice(
            currency,
            salePrice,
          )}
        </Text>
        {showTransparentText && <Text variant="caption" rootStyle={styles.addonTransparentText}>-</Text>}
      </VStack>
    )
  }

  if (priceType === 'totalPrice') {
    return (
      <VStack>
        <HStack alignItems="end">
          <Text variant="caption" color={colors.red600}>$</Text>
          <Text data-testid={dataTestId} variant="h5" color={colors.red600} align="right">
            {formatPrice(
              currency,
              salePrice * quantity,
              'value',
            )}
          </Text>
        </HStack>
      </VStack>
    )
  }

  if (priceType === 'addonSalePrice') {
    return (
      <VStack justifyContent="flex-end">
        <HStack gap={1} alignItems="flex-end">
          <HStack alignItems="flex-end">
            <Text data-testid={dataTestId} variant="caption" color={colors.red600} align="right">
              $
            </Text>
            <Text data-testid={dataTestId} variant="h5" color={colors.red600} align="right">
              {formatPrice(
                currency,
                /** addon_v2_Price 是已經乘過數量的價格，不需再乘 quantity */
                salePrice,
                'value',
              )}
            </Text>
          </HStack>
        </HStack>
        {showTransparentText && <Text variant="caption" rootStyle={styles.addonTransparentText}>-</Text>}
      </VStack>
    )
  }

  if (priceType === 'productPageAddonSalePrice') {
    return (
      <HStack alignItems="flex-end" justifyContent="flex-end">
        <Text data-testid={dataTestId} variant="caption" color={colors.red600} align="right">
          $
        </Text>
        <Text data-testid={dataTestId} variant="h5" color={colors.red600} align="right">
          {formatPrice(
            currency,
            salePrice,
            'value',
          )}
        </Text>
        <Box ml={1}>
          <Text variant="tiny" color={colors.grey500} lineThrough>
            {formatPrice(
              currency,
              originalPrice,
            )}
          </Text>
        </Box>
      </HStack>
    )
  }

  if (priceType === 'discountedPrice') {
    return (
      <HStack alignItems="flex-end" color={colors.red600} rootStyle={styles.discountedPrice}>
        <Text variant="tiny" fontWeight={600}>{t?.('cartUi.discountedPrice', { defaultValue: '最高省' })}</Text>
        <Text data-testid={dataTestId} variant="tiny" fontWeight={600} color={colors.red600}>
          $
        </Text>
        <Text data-testid={dataTestId} variant="caption" fontWeight={600} color={colors.red600}>
          {formatPrice(
            currency,
            salePrice,
            'value',
          )}
        </Text>
      </HStack>
    )
  }
}

export const CartSalePrice = (props: CartSalePriceProps) => {
  const { priceType = 'salePrice', currency = 'TWD', salePrice = 0, originalPrice = 0, quantity = 1, 'data-testid': dataTestId, showTransparentText = false, t } = props
  return priceMap(priceType, currency, salePrice, originalPrice, quantity, dataTestId, showTransparentText, t)
}
